import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_VISITA_ENTERAL, { adaptAddVisitaEnteralInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaEnteral";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

export default function ProgramaEnteral({ readOnly, onSaveRef, onExposeFormatted, programa, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [cambioIndicacion, setCambioIndicacion] = useState(false);
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(false);

  const { visita, setVisita } = useVisita();

  useEffect(() => {
    const visitaIndicaciones = visita?.listaIndicacion || [];
    const formIndicaciones = formData?.listaIndicacion || [];

    if (visitaIndicaciones.length !== formIndicaciones.length) {
      setCambioIndicacion(true);
      return;
    }

    const formMap = new Map(
      formIndicaciones.map(ind => [ind.idInsumo, ind.cantidadDiaria])
    );

    const hayCambios = visitaIndicaciones.some(visitaInd => {
      const formCantidad = formMap.get(visitaInd.idInsumo);
      // Si no existe el insumo o la cantidad es diferente
      return formCantidad === undefined || formCantidad !== visitaInd.cantidadDiaria;
    });

    setCambioIndicacion(hayCambios);
  }, [visita?.listaIndicacion, formData?.listaIndicacion]);

  const inputCombos = groups.flatMap((inputgroup) => {
    return inputgroup.inputs.filter((input) => !!input.comboName);
  });

  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataSolicitudPendiente) return;
    setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado != null);
  }, [dataSolicitudPendiente]);

  const [save] = useMutation(SAVE_VISITA_ENTERAL);

  const [proteinasTotales, caloriasTotales] = useMemo(() => {
    const indicaciones = Array.isArray(formData.listaIndicacion) ? formData.listaIndicacion : [];
    let totalProteinas = 0;
    let totalCalorias = 0;

    indicaciones.forEach((indicacion) => {
      const cantidad = Number(indicacion.cantidadDiaria) || 0;
      const proteinas = Number(indicacion.proteinas) || 0;
      const calorias = Number(indicacion.calorias) || 0;

      totalProteinas += cantidad * proteinas;
      totalCalorias += cantidad * calorias;
    });
    return [totalProteinas, totalCalorias];
  }, [formData.listaIndicacion]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      proteinasTotales,
      caloriasTotales
    }));
  }, [proteinasTotales, caloriasTotales]);

  const formattedGroups = useMemo(() => {
    const isSSAE = programa === "SSAE";
    const idComboRIValorTipoAcceso = formData?.idComboRIValorTipoAcceso;
    const sngId = combosRI?.idComboRIValorTipoAcceso?.find((c) => c.displayName.toLowerCase() === 'sng')?.id;
    const sogId = combosRI?.idComboRIValorTipoAcceso?.find((c) => c.displayName.toLowerCase() === 'orogastrica')?.id;
    const accEsSngUOrogastrica = [sngId, sogId].includes(idComboRIValorTipoAcceso);

    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (!!newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        switch (input.name) {
          case "cambioSNGoSOG":
            newInput.hidden = !accEsSngUOrogastrica;
            break;
          case "justificacionCambioSNGoSOG":
            newInput.hidden = !accEsSngUOrogastrica || formData?.cambioSNGoSOG !== false;
            newInput.required = accEsSngUOrogastrica && formData?.cambioSNGoSOG === false;
            break;
          case "justificacionAccesoEnteral":
            newInput.hidden = formData?.accesoEnteral !== false;
            newInput.required = formData?.accesoEnteral === false;
            break;
          case "negativaGastrostomia":
            newInput.hidden = !accEsSngUOrogastrica;
            break;
          case "justificacionNegativaGastrostomia":
            newInput.hidden = !accEsSngUOrogastrica || formData?.negativaGastrostomia !== true;
            newInput.required = accEsSngUOrogastrica && formData?.negativaGastrostomia === true;
            break;
          case "listaIndicacion":
            newInput.hidden = isSSAE;
            break;
          case "idComboRIValorModoInfusion":
          case "llevaBomba":
          case "hidratacion":
            newInput.hidden = isSSAE;
            break;
          case "flujo":
            newInput.hidden = isSSAE;
            newInput.required = formData?.llevaBomba;
            break;
          case "indicacionHidratacion":
            newInput.hidden = isSSAE || formData?.hidratacion !== true;
            newInput.required = !isSSAE && formData?.hidratacion === true;
            break;
          case "formaPreparacion":
            newInput.required = !isSSAE
              && Array.isArray(formData?.listaIndicacion)
              && formData.listaIndicacion?.some(ind => ind?.unidad?.toLowerCase() === "gramos" || ind?.unidad === "grs");
            newInput.hidden = !newInput.required;
            break;
          case "justificacionModificacion":
            newInput.required = cambioIndicacion && !isSSAE;
            newInput.hidden = isSSAE;
            break;
          case "complicaciones":
            const ningunaId = complicacionesCombo?.find(c => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map(opt => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some(id => id !== ningunaId);
                if (hasNinguna) {
                  // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                  return { ...opt, disabled: opt.id !== ningunaId };
                }
                if (hasOther) {
                  // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                  return { ...opt, disabled: opt.id === ningunaId };
                }
              }
              return { ...opt, disabled: false };
            });
            break;
          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia && input.name !== "justificacionModificacion") newInput.required = false;

        return newInput;
      });

      const groupHidden = !formattedInputs.some((i) => !i.hidden);

      return {
        ...group,
        hidden: groupHidden,
        inputs: formattedInputs
      };
    });
  }, [
    readOnly,
    formData,
    visita,
    programa,
    complicacionesCombo,
    formData?.listaIndicacion,
    formData?.complicaciones,
    cambioIndicacion,
    combosRI,
    esUrgencia]
  );

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({
      variables: { input: adaptAddVisitaEnteralInput(formData) }
    });

    const idVisitaEnteral = data?.saveVisitaEnteral?.idVisitaEnteral;
    if (!idVisitaEnteral) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaEnteral }));
  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    if (!visita?.idEvaluacion) return;
    
    getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
  }, [visita?.idEvaluacion]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaEnteral",
      stepName: "Programa Enteral",
      formattedInputs: undefined,
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== "listaIndicacion" ?
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={handleChange}
                        row={input.type === "multiple_boolean"} /> :


                      <IndicacionesVisitaInput
                        readOnly={readOnly}
                        handleChange={handleChange}
                        indicaciones={formData[input.name]}
                        inputName={input.name}
                        idEvaluacion={visita?.idEvaluacion}
                        solicitudIndicacionPendiente={solicitudIndicacionPendiente}
                      />

                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "",
    inputs: [
      {
        name: "idComboRIValorKarnofsky",
        displayName: "Karnofsky",
        comboName: 'Karnofsky',
        type: "select",
        required: true
      },
      {
        name: "ingestaOral",
        displayName: "Ingesta Oral",
        type: "boolean",
        lightTitle: true,
        required: true
      }]

  },
  // Grupo 2: Accesos
  {
    title: "Accesos Enterales",
    inputs: [
      {
        name: "idComboRIValorTipoAcceso",
        comboName: 'Tipo de Acceso',
        displayName: "Tipo de Acceso",
        type: "select",
        required: true
      },
      {
        name: "cambioSNGoSOG",
        displayName: "¿Cambio de SNG/SOG en domicilio?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "justificacionCambioSNGoSOG",
        displayName: "Si es NO, justificar",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "accesoEnteral",
        displayName: "Acceso Enteral en buen estado",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "justificacionAccesoEnteral",
        displayName: "Si es NO, justificar",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "negativaGastrostomia",
        displayName: "Negativa de Gastrostomía",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "justificacionNegativaGastrostomia",
        displayName: "Si es SI, justificar",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "listaIndicacion",
        displayName: "Indicaciones",
        type: "custom",
        required: true
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        type: "long_string",
        maxLength: 4000,
        required: false
      },
      {
        name: "idComboRIValorModoInfusion",
        displayName: "Modo de infusión",
        comboName: 'Modo de infusión NE',
        type: "select",
        required: true
      },
      {
        name: "llevaBomba",
        displayName: "Lleva Bomba",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "formaPreparacion",
        displayName: "Forma de preparación",
        type: "string",
        required: true
      },
      {
        name: "indicacionEspecial",
        displayName: "Indicaciones especiales",
        type: "long_string"
      },
      {
        name: "flujo",
        displayName: "Flujo (ml/h)",
        type: "int",
        required: true,
        min: 0
      },
      {
        name: "hidratacion",
        displayName: "Hidratación",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "indicacionHidratacion",
        displayName: "Indicación de Hidratación",
        type: "long_string",
        maxLength: 500
      }]

  },

  // Grupo 4: Requerimientos
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "proteinasTotales",
        displayName: "Proteínas Totales día",
        type: "float",
        min: 0,
        required: true
      },
      {
        name: "caloriasTotales",
        displayName: "Calorías Totales día",
        type: "float",
        min: 0,
        required: true
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];